import type { Order, PlaceKind } from './crowdPlaces';

/**
 * The crowd: the city's residents as figures with a home, a trade and a day (docs/CROWD.md).
 *
 * The number follows the user's request of 6 October 2026 for 10,000 people doing their thing,
 * beside the figure of about 12,000 inhabitants that museum texts give for the map of 1538. Every
 * share, weight and duration below is an estimate: no census by trade exists for 1538, and none of
 * these numbers is taken from a source.
 */
export const CROWD = {
  residents: 10_000,
  /** How many times a resident goes out between the day's opening and its close. */
  outings: [10, 16],
  /** Walking speed, in metres a second. */
  speed: [0.95, 1.35],
  /** Each walker's own lane, to the right of the middle of the way. */
  lane: [0.25, 2.2],
  scale: [0.93, 1.03],
  /** The day opens this long after sunrise and closes this long before sunset, and never before
   *  five or after nine in the evening, when the watch musters (docs/NIGHT.md). */
  dawnMarginMin: 10,
  duskMarginMin: 10,
  earliestMin: 5 * 60,
  latestMin: 21 * 60,
  /** Residents rise over this share of the day, so their days do not turn over together. */
  riseSpread: 0.15,
  /** A stay cut short to fit its part of the day is never shorter than this. */
  shortestStayS: 5,
  /** How far a figure stands from the point of a place that has no seat for it. */
  standM: 2.2,
  /** Begin and finish behind the doorway's dark recess, so walls conceal a full-size resident. */
  doorInsideM: 1.5,
  /** One who keeps a pitch or has work at a place stays at it this long at a time. */
  keepS: [5400, 12_600],
} as const;

/** How long a resident who comes to a place of each kind stays, and whether they are seen there. */
export const STAYS: Record<
  PlaceKind,
  { readonly seconds: readonly [number, number]; readonly seen: boolean }
> = {
  market: { seconds: [15, 60], seen: true },
  stall: { seconds: [15, 50], seen: true },
  square: { seconds: [8, 30], seen: true },
  quay: { seconds: [12, 55], seen: true },
  church: { seconds: [1500, 3000], seen: false },
  corner: { seconds: [8, 30], seen: true },
  gate: { seconds: [3600, 9000], seen: false },
  cloister: { seconds: [8, 30], seen: true },
  yard: { seconds: [120, 360], seen: true },
  load: { seconds: [60, 180], seen: true },
};

/* How many of the nearest places of a kind a resident chooses among: a buyer may go to any pitch
   of the market, and to a corner or a quay near home. */
export const NEAREST_OF: Record<PlaceKind, number> = {
  market: 1,
  stall: 1,
  square: 2,
  quay: 5,
  church: 2,
  corner: 12,
  gate: 2,
  cloister: 3,
  yard: 2,
  load: 2,
};

type Goes = Partial<Record<PlaceKind, number>>;

/**
 * One trade of the residents: the citizens' trade whose figures play it (docs/CITIZENS.md), its
 * share of the residents, and its day. `job` is where it has work of its own that is done in the
 * open, and the share of its outings that go there: a seller's pitch, a place at the fish benches,
 * the yard, the goods on a quay. One whose trade is plied indoors has none, and is seen on its
 * errands. `order` is the religious house it lives in. `goes` is how often its other outings go
 * to each kind of place.
 */
export interface CrowdTrade {
  readonly trade: string;
  readonly share: number;
  readonly job?: { readonly at: PlaceKind; readonly share: number };
  readonly order?: Order;
  readonly goes: Goes;
}

/* The errands of a household, of a craft, of the water, of those with means, and of the religious. */
const HOUSE: Goes = {
  market: 0.3,
  stall: 0.1,
  square: 0.05,
  corner: 0.35,
  church: 0.14,
  quay: 0.06,
};
const CRAFT: Goes = {
  market: 0.12,
  stall: 0.04,
  square: 0.08,
  corner: 0.46,
  church: 0.1,
  quay: 0.15,
  gate: 0.05,
};
const WATER: Goes = {
  quay: 0.6,
  corner: 0.18,
  square: 0.06,
  market: 0.06,
  church: 0.05,
  gate: 0.05,
};
const MEANS: Goes = {
  square: 0.22,
  market: 0.12,
  corner: 0.28,
  church: 0.2,
  quay: 0.13,
  gate: 0.05,
};
const VOWED: Goes = { cloister: 0.4, church: 0.38, corner: 0.14, market: 0.08 };

const sells = (at: PlaceKind) => ({ at, share: 0.85 });

export const CROWD_TRADES: readonly CrowdTrade[] = [
  { trade: 'cooper', share: 0.012, goes: CRAFT },
  { trade: 'carpenter', share: 0.018, job: { at: 'yard', share: 0.8 }, goes: CRAFT },
  { trade: 'shipwright', share: 0.014, job: { at: 'yard', share: 0.9 }, goes: CRAFT },
  { trade: 'smith', share: 0.01, goes: CRAFT },
  { trade: 'mason', share: 0.012, goes: CRAFT },
  { trade: 'weaver', share: 0.022, goes: CRAFT },
  { trade: 'tailor', share: 0.012, goes: CRAFT },
  { trade: 'shoemaker', share: 0.014, goes: CRAFT },
  { trade: 'brewer', share: 0.014, goes: CRAFT },
  { trade: 'labourer', share: 0.037, job: { at: 'load', share: 0.5 }, goes: CRAFT },
  { trade: 'porter', share: 0.03, job: { at: 'load', share: 0.5 }, goes: WATER },
  { trade: 'baker', share: 0.02, goes: CRAFT },
  { trade: 'butcher', share: 0.016, goes: CRAFT },
  { trade: 'innkeeper', share: 0.008, goes: CRAFT },
  { trade: 'fishmonger', share: 0.008, job: sells('stall'), goes: WATER },
  { trade: 'farmer', share: 0.012, job: sells('market'), goes: CRAFT },
  { trade: 'sailor', share: 0.07, job: { at: 'load', share: 0.75 }, goes: WATER },
  { trade: 'fisherman', share: 0.03, job: { at: 'load', share: 0.75 }, goes: WATER },
  { trade: 'shipmaster', share: 0.016, goes: WATER },
  { trade: 'merchant', share: 0.026, goes: MEANS },
  { trade: 'clerk', share: 0.012, goes: MEANS },
  { trade: 'burgher', share: 0.026, goes: MEANS },
  { trade: 'market_woman', share: 0.03, job: sells('market'), goes: HOUSE },
  { trade: 'farm_woman', share: 0.02, job: sells('market'), goes: HOUSE },
  { trade: 'bakers_wife', share: 0.02, job: sells('market'), goes: HOUSE },
  { trade: 'fishwife', share: 0.01, job: sells('stall'), goes: HOUSE },
  { trade: 'alewife', share: 0.014, goes: HOUSE },
  { trade: 'housewife', share: 0.14, goes: HOUSE },
  { trade: 'maid', share: 0.07, goes: HOUSE },
  { trade: 'washerwoman', share: 0.03, goes: { ...HOUSE, quay: 0.3 } },
  { trade: 'townswoman', share: 0.08, goes: HOUSE },
  { trade: 'gentlewoman', share: 0.022, goes: MEANS },
  { trade: 'widow', share: 0.03, goes: { ...HOUSE, church: 0.3 } },
  { trade: 'sister', share: 0.055, order: 'sister', goes: VOWED },
  { trade: 'friar', share: 0.022, order: 'friar', goes: VOWED },
  { trade: 'beguine', share: 0.012, order: 'beguine', goes: VOWED },
  { trade: 'priest', share: 0.006, goes: { church: 0.6, corner: 0.2, cloister: 0.1, square: 0.1 } },
];
